"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { CheckCircle, File, RefreshCw, Upload, X, XCircle } from "lucide-react";
import * as React from "react";
import type { UploadConfig, UploadFile, UploadHandler } from "../lib/types";
import { useUpload } from "../lib/use-upload";
import { cn, formatBytes } from "../lib/utils";

const dropzoneVariants = cva("relative rounded-lg border-2 border-dashed transition-colors cursor-pointer", {
  variants: {
    variant: {
      default: "border-muted-foreground/25 hover:border-muted-foreground/50",
      active: "border-primary bg-primary/5",
      error: "border-destructive bg-destructive/5",
    },
    size: {
      sm: "p-4",
      default: "p-6",
      lg: "p-8",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const progressVariants = cva("h-1 rounded-full transition-all", {
  variants: {
    status: {
      uploading: "bg-primary",
      processing: "bg-primary animate-pulse",
      complete: "bg-green-500",
      error: "bg-destructive",
      pending: "bg-muted",
      idle: "bg-muted",
    },
  },
  defaultVariants: {
    status: "pending",
  },
});

export interface FileUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof dropzoneVariants> {
  /**
   * Upload handler configuration
   */
  handler: UploadHandler;
  /**
   * Upload configuration (max size, accepted types, etc.)
   */
  config?: UploadConfig;
  /**
   * Called when files change (added, removed, status changed)
   */
  onChange?: (files: UploadFile[]) => void;
  /**
   * Called when all uploads complete
   */
  onComplete?: (files: UploadFile[]) => void;
  /**
   * Whether to show the file list
   * @default true
   */
  showFileList?: boolean;
  /**
   * Custom dropzone content
   */
  children?: React.ReactNode;
  /**
   * Disabled state
   */
  disabled?: boolean;
}

export function FileUpload({
  handler,
  config,
  onChange,
  onComplete,
  showFileList = true,
  children,
  disabled = false,
  className,
  size,
  ...props
}: FileUploadProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { files, isUploading, addFiles, removeFile, retryFile, clearCompleted } = useUpload({
    handler,
    ...config,
  });

  // Track changes
  const prevFilesRef = React.useRef<UploadFile[]>([]);
  React.useEffect(() => {
    if (JSON.stringify(files) !== JSON.stringify(prevFilesRef.current)) {
      prevFilesRef.current = files;
      onChange?.(files);

      // Check if all uploads are complete
      const allComplete = files.length > 0 && files.every((f) => f.status === "complete" || f.status === "error");
      if (allComplete && !isUploading) {
        onComplete?.(files);
      }
    }
  }, [files, isUploading, onChange, onComplete]);

  const handleDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      addFiles(droppedFiles);
    },
    [addFiles, disabled],
  );

  const handleClick = React.useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleFileSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      addFiles(selectedFiles);
      e.target.value = "";
    },
    [addFiles],
  );

  const acceptString = config?.accept?.join(",");

  return (
    <div className={cn("space-y-4", className)} {...props}>
      {/* Dropzone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick();
          }
        }}
        className={cn(
          dropzoneVariants({
            variant: isDragging ? "active" : "default",
            size,
          }),
          disabled && "opacity-50 cursor-not-allowed",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={config?.maxFiles !== 1}
          accept={acceptString}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {children || (
          <div className="flex flex-col items-center justify-center text-center">
            <Upload className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-1">
              <span className="font-medium text-foreground">Click to upload</span> or drag and drop
            </p>
            {config?.accept && <p className="text-xs text-muted-foreground">{config.accept.join(", ")}</p>}
            {config?.maxSize && <p className="text-xs text-muted-foreground">Max {formatBytes(config.maxSize)}</p>}
          </div>
        )}
      </div>

      {/* File list */}
      {showFileList && files.length > 0 && (
        <div className="space-y-2">
          {files.map((uploadFile) => (
            <FileUploadItem
              key={uploadFile.id}
              file={uploadFile}
              onRemove={() => removeFile(uploadFile.id)}
              onRetry={() => retryFile(uploadFile.id)}
            />
          ))}

          {/* Clear completed button */}
          {files.some((f) => f.status === "complete") && (
            <button
              type="button"
              onClick={clearCompleted}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear completed
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface FileUploadItemProps {
  file: UploadFile;
  onRemove: () => void;
  onRetry: () => void;
}

function FileUploadItem({ file, onRemove, onRetry }: FileUploadItemProps) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
      {/* Icon */}
      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
        {file.status === "complete" ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : file.status === "error" ? (
          <XCircle className="h-5 w-5 text-destructive" />
        ) : (
          <File className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium truncate">{file.file.name}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">{formatBytes(file.file.size)}</span>
        </div>

        {/* Progress bar */}
        {(file.status === "uploading" || file.status === "processing") && (
          <div className="mt-2 h-1 w-full bg-muted rounded-full overflow-hidden">
            <div className={cn(progressVariants({ status: file.status }))} style={{ width: `${file.progress}%` }} />
          </div>
        )}

        {/* Status text */}
        {file.status === "processing" && <p className="text-xs text-muted-foreground mt-1">Processing...</p>}

        {file.status === "error" && <p className="text-xs text-destructive mt-1">{file.error}</p>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {file.status === "error" && (
          <button
            type="button"
            onClick={onRetry}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        )}

        {(file.status === "pending" || file.status === "error" || file.status === "complete") && (
          <button
            type="button"
            onClick={onRemove}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

FileUpload.displayName = "FileUpload";
